import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/Layout";
import { toast } from "sonner";
import { KeyRound, Trash2, Webhook, ScrollText, Copy, Plus } from "lucide-react";

const inp = "border border-[#DFDAD0] px-2 py-1.5 text-sm font-mono outline-none focus:border-[#1C2530] bg-white";
const btn = "flex items-center gap-2 border px-3 py-2 text-xs font-mono uppercase tracking-wider transition-colors disabled:opacity-40";
const card = "border border-[#DFDAD0] bg-white p-5 space-y-3";
const h = "font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60";

function Secret({ value, label }) {
  return (
    <div className="border border-[#E2711D] bg-[#FFF7F0] p-3 space-y-1" data-testid="secret-once">
      <div className="font-mono text-[10px] uppercase text-[#E2711D]">{label} — shown once, copy it now</div>
      <div className="flex items-center gap-2">
        <code className="text-xs break-all" data-testid="secret-value">{value}</code>
        <button onClick={() => { navigator.clipboard?.writeText(value); toast.success("Copied"); }} data-testid="secret-copy" className="border border-[#DFDAD0] p-1 hover:bg-[#EFECE5]"><Copy className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

function Keys() {
  const [data, setData] = useState({ keys: [], scopes: [] });
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState([]);
  const [created, setCreated] = useState(null);
  const load = () => api.get("/integrations/api-keys").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!name.trim() || !scopes.length) { toast.error("Name and at least one scope required"); return; }
    try { const r = await api.post("/integrations/api-keys", { name, scopes }); setCreated(r.data); setName(""); setScopes([]); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const revoke = async (k) => {
    if (!window.confirm(`Revoke key "${k.name}"? Jarvis calls using it will fail immediately.`)) return;
    try { await api.delete(`/integrations/api-keys/${k.id}`); toast.success("Revoked"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className={card} data-testid="api-keys-card">
      <div className={h}>API keys · Authorization: Bearer &lt;key&gt; · 60 req/min</div>
      {created && <Secret value={created.key} label={`Key "${created.name}"`} />}
      <div className="flex flex-wrap gap-2 items-center">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Key name (e.g. Jarvis prod)" data-testid="api-key-name" className={`${inp} w-56`} />
        <button onClick={create} data-testid="api-key-create" className={`${btn} border-[#1C2530] bg-[#1C2530] text-[#F5F3EE] hover:bg-[#2b3845]`}><Plus className="w-3.5 h-3.5" /> Create key</button>
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="api-key-scopes">
        {data.scopes.map((s) => (
          <button key={s} onClick={() => setScopes((x) => x.includes(s) ? x.filter((y) => y !== s) : [...x, s])} data-testid={`scope-${s}`}
            className={`px-2 py-1 font-mono text-[10px] border ${scopes.includes(s) ? "bg-[#1C2530] text-[#F5F3EE] border-[#1C2530]" : "border-[#DFDAD0] text-[#1C2530]/60 hover:bg-[#EFECE5]"}`}>{s}</button>
        ))}
      </div>
      <table className="w-full text-xs" data-testid="api-keys-table">
        <thead><tr className="text-left font-mono text-[10px] uppercase text-[#1C2530]/50"><th className="py-1">Name</th><th>Prefix</th><th>Scopes</th><th>Last used</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {data.keys.map((k) => (
            <tr key={k.id} className="border-t border-[#DFDAD0]" data-testid={`api-key-row-${k.id}`}>
              <td className="py-2">{k.name}</td><td className="font-mono">{k.prefix}</td><td className="font-mono text-[10px]">{k.scopes.join(", ")}</td>
              <td className="font-mono text-[10px]">{k.last_used_at?.slice(0, 16) || "never"}</td>
              <td>{k.revoked_at ? <span className="text-[#E50914] font-mono text-[10px]">REVOKED</span> : <span className="text-[#3E8F99] font-mono text-[10px]">ACTIVE</span>}</td>
              <td className="text-right">{!k.revoked_at && <button onClick={() => revoke(k)} data-testid={`api-key-revoke-${k.id}`} className="text-[#1C2530]/40 hover:text-[#E50914]"><Trash2 className="w-3.5 h-3.5" /></button>}</td>
            </tr>
          ))}
          {!data.keys.length && <tr><td colSpan={6} className="py-3 text-[#1C2530]/50">No keys yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Hooks() {
  const [data, setData] = useState({ webhooks: [], events: [], deliveries: [] });
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState([]);
  const [created, setCreated] = useState(null);
  const load = () => api.get("/integrations/webhooks").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { const r = await api.post("/integrations/webhooks", { url, events }); setCreated(r.data); setUrl(""); setEvents([]); load(); }
    catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Failed (https URL + events required)"); }
  };
  const del = async (id) => { if (!window.confirm("Delete webhook?")) return; await api.delete(`/integrations/webhooks/${id}`); load(); };
  const test = async (id) => { try { const r = await api.post(`/integrations/webhooks/${id}/test`); toast[r.data?.ok ? "success" : "error"](`Test delivery → ${r.data?.status_code || r.data?.error}`); load(); } catch { toast.error("Test failed"); } };
  return (
    <div className={card} data-testid="webhooks-card">
      <div className={h}>Webhooks · signed with X-Pulse-Signature (sha256=HMAC)</div>
      {created && <Secret value={created.secret} label="Webhook secret" />}
      <div className="flex flex-wrap gap-2">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" data-testid="webhook-url" className={`${inp} w-80`} />
        <button onClick={create} data-testid="webhook-create" className={`${btn} border-[#1C2530] bg-[#1C2530] text-[#F5F3EE]`}><Webhook className="w-3.5 h-3.5" /> Register</button>
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="webhook-events">
        {data.events.map((s) => (
          <button key={s} onClick={() => setEvents((x) => x.includes(s) ? x.filter((y) => y !== s) : [...x, s])} data-testid={`event-${s}`}
            className={`px-2 py-1 font-mono text-[10px] border ${events.includes(s) ? "bg-[#1C2530] text-[#F5F3EE] border-[#1C2530]" : "border-[#DFDAD0] text-[#1C2530]/60"}`}>{s}</button>
        ))}
      </div>
      {data.webhooks.map((w) => (
        <div key={w.id} className="flex items-center justify-between border-t border-[#DFDAD0] pt-2 text-xs" data-testid={`webhook-row-${w.id}`}>
          <div><div className="font-mono break-all">{w.url}</div><div className="font-mono text-[10px] text-[#1C2530]/50">{w.events.join(", ")}</div></div>
          <div className="flex gap-2">
            <button onClick={() => test(w.id)} data-testid={`webhook-test-${w.id}`} className={`${btn} border-[#DFDAD0]`}>Test</button>
            <button onClick={() => del(w.id)} data-testid={`webhook-delete-${w.id}`} className="text-[#1C2530]/40 hover:text-[#E50914]"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      ))}
      {data.deliveries.length > 0 && (
        <div className="pt-2 border-t border-[#DFDAD0]">
          <div className={h}>Recent deliveries</div>
          {data.deliveries.slice(0, 10).map((d) => (
            <div key={d.id} className="font-mono text-[10px] flex gap-3" data-testid={`delivery-${d.id}`}><span>{d.at?.slice(0, 19)}</span><span>{d.event}</span><span className={d.ok ? "text-[#3E8F99]" : "text-[#E50914]"}>{d.status_code || d.error}</span><span className="text-[#1C2530]/50">attempt {d.attempts || 1}{d.next_retry_in_s ? ` · retry in ${d.next_retry_in_s}s` : ""}</span></div>
          ))}
        </div>
      )}
    </div>
  );
}

function Log() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/integrations/api-log").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className={card} data-testid="api-log-card">
      <div className={h}><ScrollText className="inline w-3 h-3 mr-1" />API call log · last {rows.length}</div>
      <div className="max-h-80 overflow-auto">
        <table className="w-full text-[11px] font-mono" data-testid="api-log-table">
          <thead><tr className="text-left text-[10px] uppercase text-[#1C2530]/50"><th>Time</th><th>Key</th><th>Method</th><th>Path</th><th>Status</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} className="border-t border-[#DFDAD0]"><td className="py-1">{r.at?.slice(0, 19)}</td><td>{r.key_name || "—"}</td><td>{r.method}</td><td>{r.path}</td>
              <td className={r.status >= 400 ? "text-[#E50914]" : "text-[#3E8F99]"}>{r.status}</td></tr>
          ))}</tbody>
        </table>
        {!rows.length && <div className="text-xs text-[#1C2530]/50 py-2">No calls yet.</div>}
      </div>
    </div>
  );
}

export default function ApiKeys() {
  const base = process.env.REACT_APP_BACKEND_URL;
  return (
    <>
      <PageHeader eyebrow="INTEGRATIONS" title="API keys" />
      <div className="p-6 md:p-8 space-y-6 max-w-5xl" data-testid="api-keys-page">
        <div className="text-sm text-[#1C2530]/70 font-mono" data-testid="api-base-url">
          <KeyRound className="inline w-3.5 h-3.5 mr-1" />Base URL <code>{base}/api/v1</code> · <a className="underline" href={`${base}/api/v1/docs`} target="_blank" rel="noreferrer" data-testid="api-docs-link">Docs</a> · <a className="underline" href={`${base}/api/v1/openapi.json`} target="_blank" rel="noreferrer" data-testid="api-openapi-link">OpenAPI</a>
          <div className="text-[10px] mt-1 text-[#E2711D]">Apollo-sourced fields are withheld from the API by default (restricted_fields). Only a super-admin can change that policy.</div>
        </div>
        <Keys />
        <Hooks />
        <Log />
      </div>
    </>
  );
}
